import {
  buildBrandSections,
  buildInfluencerSections,
  buildInfluencerCta,
} from '../../components/sidebars/sidebarNavConfig';
import {
  BRAND_ANALYTICS_TABS,
  getBrandAnalyticsPath,
} from '../brand/brandInsightPages';
import {
  INFLUENCER_ANALYTICS_TABS,
  getInfluencerAnalyticsPath,
} from '../influencer/influencerInsightPages';
import {
  getBrandApplicationDetailsUrl,
  getBrandCollaborationStatusUrl,
  getBrandDetailUrl,
  getBrandInfluencerDetailsUrl,
  getCampaignDetailUrl,
  getCollaborationFlowUrl,
  getInfluencerApplicationDetailsUrl,
  getInfluencerCollaborationDetailsUrl,
  getMarketplaceCampaignDetailUrl,
} from '../shared/slugify';
import {
  fetchWorkQueueData,
  getWorkQueueStatusKey,
} from '../../hooks/collaborations/useBrandCollaborationWorkQueue';
import {
  fetchInfluencerApplications,
  fetchInfluencerInvites,
} from '../../hooks/collaborations/useInfluencerCollaborations';
import { fetchFollowedBrandsForOmnibar } from '../../hooks/influencer/useFollowedBrands';
import { fetchFavoritedCampaignsForOmnibar } from '../../hooks/influencer/useFavoritedCampaigns';
import { fetchBrandCampaignsForOmnibar } from './dashboardOmnibarCampaignsCache';
import { getMarketplaceSearchPath } from '../marketplace/marketplaceKeywordSearch';
import { ADMIN_OMNIBAR_DESTINATIONS } from './adminOmnibarDestinations';

const normalizeStatus = (status) => {
  const raw = String(status || '').toLowerCase();
  if (raw === 'submitted') return 'pending';
  if (raw === 'approved') return 'accepted';
  if (raw === 'cancelled') return 'canceled';
  return raw;
};

const brandCollabHref = (item, type, collaborationStatuses) => {
  const statusRecord = collaborationStatuses[getWorkQueueStatusKey(type, item.id)];
  const acceptanceId = statusRecord?.acceptanceId ?? statusRecord?.acceptance_id;
  const influencerName = item.influencer_name || item.full_name || 'creator';
  const campaignName = item.campaign_name || item.product_name || 'campaign';

  if (acceptanceId) {
    return getBrandCollaborationStatusUrl(acceptanceId, influencerName, campaignName);
  }
  if (type === 'application') {
    return getBrandApplicationDetailsUrl(item.id, campaignName, influencerName);
  }
  return getBrandInfluencerDetailsUrl(
    item.influencer_id,
    influencerName,
    item.campaign_id,
    item.id,
  );
};

const influencerCollabHref = (item, type) => {
  const acceptanceId = item.acceptance_id ?? item.acceptanceId;
  const campaignName = item.campaign_name || item.product_name || 'campaign';
  const brandName = item.brand_name || item.company_name || 'brand';

  if (acceptanceId) {
    return getCollaborationFlowUrl(acceptanceId, campaignName, brandName);
  }
  if (type === 'application') {
    return getInfluencerApplicationDetailsUrl(item.id, campaignName, brandName);
  }
  return getInfluencerCollaborationDetailsUrl(item.id, campaignName, brandName);
};

const pushSectionDestinationRows = (rows, sections) => {
  sections.forEach((section) => {
    section.items.forEach((item) => {
      rows.push({
        id: `dest-${item.id}`,
        kind: 'destination',
        label: item.label,
        description: 'Page',
        href: item.to,
        searchHaystack: `${item.label} ${item.to}`,
      });
    });
  });
};

const buildDestinationRows = (userType) => {
  if (userType === 'admin') {
    return ADMIN_OMNIBAR_DESTINATIONS;
  }

  const rows = [];

  if (userType === 'influencer') {
    pushSectionDestinationRows(rows, buildInfluencerSections());
    const cta = buildInfluencerCta();
    rows.push({
      id: 'dest-discover',
      kind: 'destination',
      label: cta.label,
      description: 'Page',
      href: cta.to,
      searchHaystack: `${cta.label} discover marketplace campaigns`,
    });
    INFLUENCER_ANALYTICS_TABS.forEach((tab) => {
      rows.push({
        id: `dest-analytics-${tab.value}`,
        kind: 'destination',
        label: `Analytics · ${tab.label}`,
        description: 'Page',
        href: getInfluencerAnalyticsPath(tab.value),
        searchHaystack: `analytics ${tab.label} ${tab.value}`,
      });
    });
    return rows;
  }

  pushSectionDestinationRows(rows, buildBrandSections());
  rows.push({
    id: 'dest-create-campaign',
    kind: 'destination',
    label: 'Create campaign',
    description: 'Page',
    href: '/dashboard/campaigns/create',
    searchHaystack: 'create campaign post new',
  });
  rows.push({
    id: 'dest-browse-influencers',
    kind: 'destination',
    label: 'Browse influencers',
    description: 'Page',
    href: '/marketplace/influencers',
    searchHaystack: 'browse influencers marketplace creators',
  });
  rows.push({
    id: 'dest-creator-map',
    kind: 'destination',
    label: 'Influencer map',
    description: 'Page',
    href: '/creator-map',
    searchHaystack: 'influencer map creator map',
  });
  BRAND_ANALYTICS_TABS.forEach((tab) => {
    rows.push({
      id: `dest-analytics-${tab.value}`,
      kind: 'destination',
      label: `Analytics · ${tab.label}`,
      description: 'Page',
      href: getBrandAnalyticsPath(tab.value),
      searchHaystack: `analytics ${tab.label} ${tab.value}`,
    });
  });
  return rows;
};

const mapBrandEntities = ({ campaigns, applications, invites, collaborationStatuses }) => {
  const entities = [];

  (campaigns || []).forEach((campaign) => {
    const name = campaign.product_name || campaign.campaign_name || 'Campaign';
    entities.push({
      id: `entity-campaign-${campaign.id}`,
      kind: 'entity',
      entityType: 'campaign',
      type: 'campaign',
      status: normalizeStatus(campaign.status),
      label: name,
      description: 'Campaign',
      href: getCampaignDetailUrl(campaign.id, name),
      campaignName: name,
      searchHaystack: [
        name,
        campaign.product_description,
        campaign.platform,
      ].filter(Boolean).join(' '),
    });
  });

  (applications || []).forEach((item) => {
    const campaignName = item.campaign_name || item.product_name || 'campaign';
    const creatorName = item.influencer_name || item.full_name || 'creator';
    entities.push({
      id: `entity-application-${item.id}`,
      kind: 'entity',
      entityType: 'application',
      type: 'application',
      status: normalizeStatus(item.status),
      label: `${creatorName} · ${campaignName}`,
      description: 'Application',
      href: brandCollabHref(item, 'application', collaborationStatuses || {}),
      campaignName,
      creatorName,
      searchHaystack: `${creatorName} ${campaignName} application`,
    });
  });

  (invites || []).forEach((item) => {
    const campaignName = item.campaign_name || item.product_name || 'campaign';
    const creatorName = item.influencer_name || item.full_name || 'creator';
    entities.push({
      id: `entity-invite-${item.id}`,
      kind: 'entity',
      entityType: 'invite',
      type: 'invite',
      status: normalizeStatus(item.status),
      label: `${creatorName} · ${campaignName}`,
      description: 'Invite',
      href: brandCollabHref(item, 'invite', collaborationStatuses || {}),
      campaignName,
      creatorName,
      searchHaystack: `${creatorName} ${campaignName} invite`,
    });
  });

  return entities;
};

const mapInfluencerEntities = ({
  applications,
  invites,
  followedBrands,
  savedCampaigns,
}) => {
  const entities = [];

  (applications || []).forEach((item) => {
    const campaignName = item.campaign_name || item.product_name || 'campaign';
    const brandName = item.brand_name || item.company_name || 'brand';
    entities.push({
      id: `entity-application-${item.id}`,
      kind: 'entity',
      entityType: 'application',
      type: 'application',
      status: normalizeStatus(item.status),
      label: `${campaignName} · ${brandName}`,
      description: 'Application',
      href: influencerCollabHref(item, 'application'),
      campaignName,
      brandName,
      searchHaystack: `${campaignName} ${brandName} application`,
    });
  });

  (invites || []).forEach((item) => {
    const campaignName = item.campaign_name || item.product_name || 'campaign';
    const brandName = item.brand_name || item.company_name || 'brand';
    entities.push({
      id: `entity-invite-${item.id}`,
      kind: 'entity',
      entityType: 'invite',
      type: 'invite',
      status: normalizeStatus(item.status),
      label: `${campaignName} · ${brandName}`,
      description: 'Invite',
      href: influencerCollabHref(item, 'invite'),
      campaignName,
      brandName,
      searchHaystack: `${campaignName} ${brandName} invite`,
    });
  });

  (followedBrands || []).forEach((brand) => {
    const brandName = brand.company_name || brand.brand_name || 'Brand';
    const brandId = brand.brand_id ?? brand.id ?? brand.user_id;
    if (brandId == null) return;
    entities.push({
      id: `entity-followed-${brandId}`,
      kind: 'entity',
      entityType: 'followed-brand',
      type: 'brand',
      label: brandName,
      description: 'Followed brand',
      href: getBrandDetailUrl(brandId, brandName),
      brandName,
      searchHaystack: `${brandName} followed brand`,
    });
  });

  (savedCampaigns || []).forEach((fav) => {
    const campaignId = fav.campaign_id ?? fav.id;
    const campaignName = fav.product_name || fav.campaign_name || 'Campaign';
    const brandName = fav.company_name || fav.brand_name || '';
    if (campaignId == null) return;
    entities.push({
      id: `entity-saved-${campaignId}`,
      kind: 'entity',
      entityType: 'saved-campaign',
      type: 'campaign',
      label: brandName ? `${campaignName} · ${brandName}` : campaignName,
      description: 'Saved campaign',
      href: getMarketplaceCampaignDetailUrl(campaignId, campaignName),
      campaignName,
      brandName,
      searchHaystack: `${campaignName} ${brandName} saved campaign`,
    });
  });

  return entities;
};

/**
 * Pure index builder from already-fetched payloads (testable).
 */
export const buildDashboardOmnibarIndexFromData = (userType, data = {}) => {
  const destinations = buildDestinationRows(userType);
  let entities = [];

  if (userType === 'admin') {
    return {
      destinations,
      entities,
      marketplacePath: '/admin/users',
    };
  }

  if (userType === 'influencer') {
    entities = mapInfluencerEntities(data);
  } else {
    entities = mapBrandEntities(data);
  }

  return {
    destinations,
    entities,
    marketplacePath: getMarketplaceSearchPath(userType),
  };
};

/**
 * Load role-scoped data (uses shared module caches) and build the searchable index.
 */
export async function loadDashboardOmnibarIndex(userType, { force = false } = {}) {
  if (userType === 'admin') {
    return buildDashboardOmnibarIndexFromData('admin');
  }

  if (userType === 'influencer') {
    const [invites, applications, followedBrands, savedCampaigns] = await Promise.all([
      fetchInfluencerInvites(force).catch(() => []),
      fetchInfluencerApplications(force).catch(() => []),
      fetchFollowedBrandsForOmnibar(force).catch(() => []),
      fetchFavoritedCampaignsForOmnibar(force).catch(() => []),
    ]);
    return buildDashboardOmnibarIndexFromData('influencer', {
      invites,
      applications,
      followedBrands,
      savedCampaigns,
    });
  }

  const [workQueue, campaigns] = await Promise.all([
    fetchWorkQueueData(force).catch(() => ({
      applications: [],
      invites: [],
      collaborationStatuses: {},
    })),
    fetchBrandCampaignsForOmnibar(force).catch(() => []),
  ]);

  return buildDashboardOmnibarIndexFromData('brand', {
    campaigns,
    applications: workQueue.applications,
    invites: workQueue.invites,
    collaborationStatuses: workQueue.collaborationStatuses,
  });
}
